import Axios from "axios";

export const PARTIAL_FILL_FLAG_NAME = "partial fill";

export interface CreatePartialFillRequest {
    orderId: number;
    storeId: number;
    rxNumber: number;
    rfNumber: number;
    dispensedQty: number;
    promisedDate?: string | null;
}

export interface CompletionItem {
    id: number;
    parentOrderId: number;
    originalParentOrderId?: number | null;
    childOrderId?: number | null;
    storeId: number;
    rxNumber: number;
    rfNumber: number;
    originalPrescribedQty: number;
    dispensedQty: number;
    remainingQty: number;
    status: CompletionItemStatus;
    promisedDate?: string | null;
    cancellationReason?: string | null;
    fulfilledDate?: string | null;
    fulfilledBy?: string | null;
    cancelledDate?: string | null;
    cancelledBy?: string | null;
    insuranceReviewRequired: boolean;
}

export enum CompletionItemStatus {
    Pending = 0,
    Fulfilled = 1,
    Cancelled = 2,
    Undone = 3,
}

export function completionItemStatusLabel(status: CompletionItemStatus): string {
    switch (status) {
        case CompletionItemStatus.Pending: return "Pending";
        case CompletionItemStatus.Fulfilled: return "Fulfilled";
        case CompletionItemStatus.Cancelled: return "Cancelled";
        case CompletionItemStatus.Undone: return "Undone";
        default: return "Unknown";
    }
}

export function completionItemStatusVariant(status: CompletionItemStatus): string {
    switch (status) {
        case CompletionItemStatus.Pending: return "warning";
        case CompletionItemStatus.Fulfilled: return "success";
        case CompletionItemStatus.Cancelled: return "danger";
        case CompletionItemStatus.Undone: return "secondary";
        default: return "secondary";
    }
}

export interface FulfillCompletionItemRequest {
    childOrderId: number;
    dispensedQty?: number | null;
    promisedDate?: string | null;
}

export interface FulfillResult {
    fulfilledItem: CompletionItem;
    newCompletionItem?: CompletionItem | null;
}

export interface CreateCompletionOrderRequest {
    dispensedQty?: number | null;
    promisedDate?: string | null;
}

export interface CreateCompletionOrderResponse {
    orderId: number;
    fulfillResult: FulfillResult;
}

export interface CompletionItemQueueView extends CompletionItem {
    patientId: number;
    patientFirstName?: string | null;
    patientLastName?: string | null;
    drugName?: string | null;
    ndc?: string | null;
    packageId?: number | null;
    drugSource?: number | null;
    programId?: number | null;
    storeName?: string | null;
}

export default class CompletionItemService {
    static async create(request: CreatePartialFillRequest): Promise<CompletionItem> {
        const response = await Axios.post<CompletionItem>("/api/completionitem", request);
        return response.data;
    }

    static async getByOrderId(orderId: number): Promise<CompletionItem[]> {
        const response = await Axios.get<CompletionItem[]>(`/api/completionitem/order/${orderId}`);
        return response.data;
    }

    static async getByChildOrderId(childOrderId: number): Promise<CompletionItem | null> {
        const response = await Axios.get<CompletionItem | null>(`/api/completionitem/child-order/${childOrderId}`);
        return response.data;
    }

    static async getPendingForQueue(storeId?: number, programId?: number): Promise<CompletionItemQueueView[]> {
        const params: Record<string, number> = {};
        if (storeId) params.storeId = storeId;
        if (programId) params.programId = programId;
        const response = await Axios.get<CompletionItemQueueView[]>("/api/completionitem/pending/queue", { params });
        return response.data;
    }

    static async fulfill(id: number, request: FulfillCompletionItemRequest): Promise<FulfillResult> {
        const response = await Axios.put<FulfillResult>(`/api/completionitem/${id}/fulfill`, request);
        return response.data;
    }

    static async cancel(id: number, cancellationReason: string): Promise<CompletionItem> {
        const response = await Axios.put<CompletionItem>(`/api/completionitem/${id}/cancel`, { cancellationReason });
        return response.data;
    }

    static async undo(id: number): Promise<void> {
        await Axios.delete(`/api/completionitem/${id}/undo`);
    }

    static async acknowledgeInsuranceReview(id: number): Promise<void> {
        await Axios.put(`/api/completionitem/${id}/acknowledge-insurance-review`);
    }

    static async createOrder(
        id: number,
        request: CreateCompletionOrderRequest = {},
    ): Promise<CreateCompletionOrderResponse> {
        const response = await Axios.post<CreateCompletionOrderResponse>(
            `/api/completionitem/${id}/create-order`, request,
        );
        return response.data;
    }
}
